import { useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { Link, useParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { PageHeader } from '@/app/layouts/PageHeader'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { ErrorState } from '@/components/ui/EmptyState'
import { CenteredSpinner } from '@/components/ui/Spinner'
import { useToast } from '@/components/ui/Toast'
import {
  CANNOT_SEND_TEXT,
  MessageBubble,
  MessageInput,
  useConversation,
  useConversationMessages,
  useMarkConversationRead,
  usePollNewMessages,
  useSendMessage,
} from '@/features/messages'
import { useT } from '@/i18n'

/** 两条消息隔了这么久，就在后一条下面显示时间 */
const TIME_GAP_MS = 5 * 60 * 1000

/**
 * 对话页。消息正序排列，底部是输入框；进来时滚到最底，新消息到了也跟着到底
 * （除非人正往上翻着看旧的）。打开即标记已读，之后每来一条对方的消息再标一次。
 */
export function ConversationPage() {
  const t = useT()
  const toast = useToast()
  const { conversationId = '' } = useParams()
  const conversation = useConversation(conversationId)
  const messages = useConversationMessages(conversationId)
  const send = useSendMessage(conversationId)
  const markRead = useMarkConversationRead(conversationId)
  const bottomRef = useRef<HTMLDivElement>(null)
  const stickToBottom = useRef(true)

  // 缓存里新的在前；显示要旧的在前
  const ordered = useMemo(
    () => (messages.data?.pages.flatMap((page) => page.items) ?? []).slice().reverse(),
    [messages.data],
  )
  const latest = ordered.at(-1)
  const latestFromOther = [...ordered].reverse().find((m) => !m.is_mine)

  usePollNewMessages(conversationId, latest?.id, Boolean(conversation.data))

  // 对方的新消息到了（包括刚打开时）就标记已读
  const markReadRef = useRef(markRead.mutate)
  markReadRef.current = markRead.mutate
  useEffect(() => {
    if (latestFromOther) markReadRef.current(latestFromOther.id)
  }, [latestFromOther?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // 记住人是不是停在底部：往上翻着看旧消息时，新消息来了不把他拽下去
  useEffect(() => {
    const onScroll = () => {
      const distance = document.documentElement.scrollHeight - window.scrollY - window.innerHeight
      stickToBottom.current = distance < 120
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useLayoutEffect(() => {
    if (stickToBottom.current) bottomRef.current?.scrollIntoView({ block: 'end' })
  }, [latest?.id])

  if (conversation.isError) {
    const notFound = conversation.error instanceof ApiError && conversation.error.status === 404
    return (
      <>
        <PageHeader title={t('messages.title')} back />
        <ErrorState
          message={notFound ? t('messages.notFound') : conversation.error.message}
          onRetry={notFound ? undefined : () => conversation.refetch()}
        />
      </>
    )
  }

  if (conversation.isPending) {
    return (
      <>
        <PageHeader title={t('messages.title')} back />
        <CenteredSpinner />
      </>
    )
  }

  const { other } = conversation.data
  const reason = conversation.data.cannot_send_reason

  return (
    <div className="flex min-h-[calc(100dvh-4rem)] flex-col sm:min-h-dvh">
      <PageHeader
        back
        title={
          <Link to={`/${other.username}`} className="flex items-center gap-2 hover:underline">
            <Avatar src={other.avatar_url} alt={other.display_name} size="xs" className="size-7" />
            <span className="truncate">{other.display_name}</span>
          </Link>
        }
        subtitle={`@${other.username}`}
      />

      <div className="flex-1 space-y-2 py-4">
        {messages.hasNextPage && (
          <div className="flex justify-center pb-2">
            <Button
              variant="outline"
              size="sm"
              loading={messages.isFetchingNextPage}
              onClick={() => {
                stickToBottom.current = false
                void messages.fetchNextPage()
              }}
            >
              {t('messages.loadOlder')}
            </Button>
          </div>
        )}
        {messages.isPending && <CenteredSpinner />}
        {!messages.isPending && ordered.length === 0 && (
          <p className="px-4 py-8 text-center text-[15px] text-ink-muted">
            {t('messages.startHint', { username: other.username })}
          </p>
        )}
        {ordered.map((message, index) => {
          const next = ordered[index + 1]
          // 一串连续的消息只在最后一条下面显示时间，隔久了或换人了再显示
          const showTime =
            !next ||
            next.is_mine !== message.is_mine ||
            new Date(next.created_at).getTime() - new Date(message.created_at).getTime() >
              TIME_GAP_MS
          return <MessageBubble key={message.id} message={message} showTime={showTime} />
        })}
        <div ref={bottomRef} />
      </div>

      {/* 手机上底部有 tab 栏（64px）和 iPhone 的横条安全区，输入框贴在它们上面 */}
      <div className="sticky bottom-[calc(4rem+env(safe-area-inset-bottom))] border-t border-line bg-surface px-4 py-3 sm:bottom-0">
        {reason ? (
          <p className="text-center text-[15px] text-ink-muted">
            {t(CANNOT_SEND_TEXT[reason], { username: other.username })}
          </p>
        ) : (
          <MessageInput
            sending={send.isPending}
            onSend={(content, done) => {
              stickToBottom.current = true
              send.mutate(
                { content, after: latest?.id },
                {
                  onSuccess: done,
                  onError: (error) =>
                    toast.error(
                      error instanceof ApiError ? error.message : t('toast.actionFailed'),
                    ),
                },
              )
            }}
          />
        )}
      </div>
    </div>
  )
}
